import type { InactiveMemberEntity } from "@/modules/boxDashboard/modules/InactiveMembers/entities/inactiveMemberEntity"
import type { HUImage } from "@/modules/types/imageTypes"

export type InactiveMemberModel = {
  memberId: string
  fullName: string
  avatar: HUImage | null
  purchaseName: string | null
  memberSince: string | null
  lastSession: string | null
  inactiveDaysLabel: string
  numberOfDays: number
}

export type InactiveMembersBlockModel = {
  members: InactiveMemberModel[]
  totalCount: number
  hasMembers: boolean
  isEmpty: boolean
  showShowMore: boolean
  showShowLess: boolean
  showPagination: boolean
  currentPage: number
  totalPages: number
  displayedCount: number
}

function createInactiveMemberModel(entity: InactiveMemberEntity): InactiveMemberModel {
  return {
    memberId: entity.memberId,
    fullName: entity.getFullName(),
    avatar: entity.avatar,
    purchaseName: entity.purchaseName,
    memberSince: entity.getInscriptionDateFormatted(),
    lastSession: entity.getLastSlotDateFormatted(),
    inactiveDaysLabel: entity.getInactiveDaysLabel(),
    numberOfDays: entity.numberOfDays,
  }
}

export type CreateInactiveMembersBlockModelParams = {
  members: InactiveMemberEntity[]
  totalCount: number
  currentPage: number
  pageSize: number
  isExpanded: boolean
}

const INITIAL_DISPLAY_COUNT = 6

export function createInactiveMembersBlockModel(
  params: CreateInactiveMembersBlockModelParams,
): InactiveMembersBlockModel {
  const { members, totalCount, currentPage, pageSize, isExpanded } = params

  const memberModels = members.map(createInactiveMemberModel)
  const hasMembers = totalCount > 0
  const isEmpty = totalCount === 0

  // Determine display logic based on expansion and pagination
  let displayedMembers: InactiveMemberModel[]
  let displayedCount: number

  if (isExpanded) {
    // When expanded, show all results for the current page
    displayedMembers = memberModels
    displayedCount = displayedMembers.length
  } else {
    // When collapsed, show first 6 members
    displayedCount = Math.min(memberModels.length, INITIAL_DISPLAY_COUNT)
    displayedMembers = memberModels.slice(0, displayedCount)
  }

  // Show "Show More" if more than 6 members and not expanded
  const showShowMore = !isExpanded && totalCount > INITIAL_DISPLAY_COUNT

  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    members: displayedMembers,
    totalCount,
    hasMembers,
    isEmpty,
    showShowMore,
    showShowLess: isExpanded,
    showPagination: totalPages > 1,
    currentPage,
    totalPages,
    displayedCount,
  }
}
